import moment from 'moment';
import Request from '../../utilities/Request';
import { clearDb, deleteDb, openDbOrMemory } from '../../utilities/localFirst/db';
import { createPersistedStore, recordKey } from '../../utilities/localFirst/persistedStore';
import { createOp, createOpQueue, groupKey } from '../../utilities/localFirst/opQueue';
import { createFetchSender, withTimeout } from '../../utilities/localFirst/sendOps';
import uuid from '../../utilities/localFirst/uuid';
import { OP_ENDPOINTS, OPS_SYNC_TAG } from '../localFirstEndpoints';

/**
 * The food store's loader (docs/local-first-food-data.md, phase 2): the budget's calendar, meal
 * ideas and grocery lists, restored from the browser's copy and refreshed from food/snapshot.
 *
 * Every request names the budget, and every answer is dropped unless it echoes the budget the store
 * is still showing, from the same load: a budget switched while a snapshot is in flight never shows
 * the old one's data.
 *
 * This module imports nothing that imports the store, since the food reducer imports it while the
 * store is being built.
 */

/**
 * The version of the persisted copy's shape, in its key. A copy of a lower one is deleted on load.
 * 2: meals keyed by uid (phase 4).
 */
export const FOOD_SCHEMA = 2;
export const FOOD_NAMESPACE = 'food';
/** The budgets per user whose copy is kept. */
export const KEEP_BUDGETS = 5;
/** A copy older than this is refreshed when the page becomes visible. */
export const STALE_AFTER_SECONDS = 60;
/** How often the Groceries tab refreshes while it is shown (pollFood). */
export const FOOD_POLL_SECONDS = 30;
/** The most months one food/range request asks for. */
export const MAX_RANGE_MONTHS = 4;
/**
 * How long food/snapshot or food/range may take before it is given up as failed. A refresh in
 * flight holds every later one back (they wait for it), so one that never answers must end.
 */
export const FOOD_REQUEST_TIMEOUT_MS = 30 * 1000;

export const FOOD_CLEARED = 'FOOD_CLEARED';
export const FOOD_RESTORED = 'FOOD_RESTORED';
export const FOOD_SNAPSHOT_BEGIN = 'FOOD_SNAPSHOT_BEGIN';
export const FOOD_SNAPSHOT_SUCCESS = 'FOOD_SNAPSHOT_SUCCESS';
export const FOOD_SNAPSHOT_FAIL = 'FOOD_SNAPSHOT_FAIL';
export const FOOD_REFRESH_DIRTY = 'FOOD_REFRESH_DIRTY';
export const FOOD_RANGE_BEGIN = 'FOOD_RANGE_BEGIN';
export const FOOD_RANGE_LOADED = 'FOOD_RANGE_LOADED';
export const FOOD_RANGE_FAIL = 'FOOD_RANGE_FAIL';
export const FOOD_BOUGHT_LISTS_LOADED = 'FOOD_BOUGHT_LISTS_LOADED';
export const FOOD_CHANGES_RECEIVED = 'FOOD_CHANGES_RECEIVED';
/** Phase 3: ops (docs/local-first-food-data.md, "Rebase"). */
export const FOOD_OP_ENQUEUED = 'FOOD_OP_ENQUEUED';
export const FOOD_OP_APPLIED = 'FOOD_OP_APPLIED';
export const FOOD_OP_REJECTED = 'FOOD_OP_REJECTED';
export const FOOD_OPS_RESTORED = 'FOOD_OPS_RESTORED';
export const FOOD_RESULTS_FOLDED = 'FOOD_RESULTS_FOLDED';
/** Folded results deleted from the op_results store, so the store need not remember them. */
export const FOOD_RESULTS_PRUNED = 'FOOD_RESULTS_PRUNED';
export const FOOD_SYNC_STATE = 'FOOD_SYNC_STATE';
export const FOOD_REJECTED_DISMISSED = 'FOOD_REJECTED_DISMISSED';
/**
 * The food store has moved to another user or budget by a route other than a budget switch (a
 * cached player answer, a sign-in as someone else, a logout). The calendar and meal ideas slices
 * empty what they hold of the last one, as they do on CHANGE_ACTIVE_BUDGET_SUCCESS. Dispatched by
 * food/legacyReset.js.
 */
export const FOOD_LEGACY_RESET = 'FOOD_LEGACY_RESET';

export const foodKey = (userId, budgetId) => recordKey({ namespace: FOOD_NAMESPACE, schema: FOOD_SCHEMA, userId, budgetId });

/**
 * What the thunks persist through, and their clock. The page opens the database once; tests set
 * their own with setFoodEnvironment(), over memoryDb().
 *
 * `session` is this tab's session marker once loadFood() has opened it; `channel` carries the
 * logout to other tabs; `queue` is the op queue (utilities/localFirst/opQueue.js), which sends only
 * the session's user's ops.
 */
let environment = null;
let environmentReady = null;

const defaults = () => ({
	now: () => Date.now(),
	requestTimeoutMs: FOOD_REQUEST_TIMEOUT_MS,
	session: null,
	channel: null,
	db: null,
	persisted: null,
	queue: null,
});

/**
 * Where the page's op queue reports, set by startFood(), which has the store: results, ops another
 * tab made, and its state; and where the user is told of changes the server refused
 * (tellFoodRejections). Nothing until then.
 */
const queueHooks = { onResult: () => {}, onEnqueued: () => {}, onState: () => {}, onRejected: () => {} };

export function setFoodQueueHooks(hooks) {
	queueHooks.onResult = (hooks && hooks.onResult) || (() => {});
	queueHooks.onEnqueued = (hooks && hooks.onEnqueued) || (() => {});
	queueHooks.onState = (hooks && hooks.onState) || (() => {});
	queueHooks.onRejected = (hooks && hooks.onRejected) || (() => {});
}

/** Asks the service worker to send the queue when the connection comes back, where it can. */
const requestBackgroundSync = () => {
	if(typeof navigator === 'undefined' || !navigator.serviceWorker || typeof window === 'undefined' || !('SyncManager' in window)) {
		return;
	}
	navigator.serviceWorker.ready
		.then((registration) => (registration.sync ? registration.sync.register(OPS_SYNC_TAG) : undefined))
		.catch((error) => console.error(error));
};

/**
 * The page's op queue over `env`'s database, sending as the session's user and reporting to the
 * hooks startFood() sets. `options` replace any of createOpQueue's, for a spec.
 */
export const createFoodQueue = (env, options = {}) => createOpQueue({
	db: env.db,
	send: createFetchSender({ endpoints: OP_ENDPOINTS, baseUrl: () => `${Request.getSiteUrl()}/api/v1` }),
	currentUserId: () => (env.session ? env.session.userId : null),
	onResult: (record, meta) => queueHooks.onResult(record, meta),
	onEnqueued: (op, meta) => queueHooks.onEnqueued(op, meta),
	onState: (state) => queueHooks.onState(state),
	requestSync: requestBackgroundSync,
	...options,
});

export function setFoodEnvironment(next) {
	environment = next ? { ...defaults(), ...next } : null;
	environmentReady = environment ? Promise.resolve(environment) : null;
	return environment;
}

const openChannel = () => {
	try {
		return typeof BroadcastChannel === 'function' ? new BroadcastChannel('local-first') : null;
	} catch(error) {
		return null;
	}
};

/** The environment, opening the database the first time. */
export function foodEnvironmentReady() {
	if(!environmentReady) {
		environmentReady = openDbOrMemory().then((db) => {
			environment = { ...defaults(), db, persisted: createPersistedStore({ db }), channel: openChannel() };
			environment.queue = createFoodQueue(environment);
			return environment;
		});
	}
	return environmentReady;
}

const now = () => (environment ? environment.now() : Date.now());

/** A food read, failing with TIMED_OUT when it has not answered in the environment's requestTimeoutMs. */
const foodGet = (action, data) => withTimeout(Request.apiv1Get(action, data),
	(environment && environment.requestTimeoutMs) || FOOD_REQUEST_TIMEOUT_MS);
const nowSeconds = () => Math.floor(now() / 1000);

/** The food store's clock, in unix seconds: when an op says it was made. */
export const foodNowSeconds = nowSeconds;

const errorMessage = (error) => (error && error.responseJSON && error.responseJSON.error) ||
	(error && error.message) || 'The food data could not be loaded.';

/** Whether the store holds a budget's food data, or is loading one. Takes a thunk's getState. */
export const isFoodActive = (getState) => {
	const state = typeof getState === 'function' ? getState() : null;
	return !!(state && state.food && state.food.budgetId !== null && state.food.budgetId !== undefined);
};

/** The signed-in user and the budget they are on, from the player, or null. */
const playerIds = (state) => {
	const player = state.dashboard && state.dashboard.player;
	if(!player || !player.id || !player.active_budget || !player.active_budget.id) {
		return null;
	}
	return { userId: Number(player.id), budgetId: Number(player.active_budget.id) };
};

/**
 * The tab's session marker for `userId`, opening it when the tab has none for that user. When the
 * marker cannot be written (a full disk, or the browser's IndexedDB failing), the tab still has a
 * session of its own, in memory, marked `memory`: its ops are still sent as that user's, from the
 * queue's memory if need be, while no copy is persisted, since every save checks the stored marker.
 */
const openSession = (env, userId) => {
	if(env.session && String(env.session.userId) === String(userId)) {
		return Promise.resolve(env.session);
	}
	return env.persisted.openSession(userId).then((session) => {
		env.session = session;
		return session;
	}, (error) => {
		console.error(error);
		env.session = { userId, sessionId: null, memory: true };
		return env.session;
	});
};

/**
 * Each store's latest load, `{ loadId, promise, restoring }`, so a second caller waits for it rather
 * than start another: `restoring` until the browser's copy has been read and the refresh starts.
 * And each store's refresh in flight.
 */
const loads = new WeakMap();
const refreshes = new WeakMap();

/** The store's load for the food store's current loadId, or null. */
const currentLoad = (getState) => {
	const load = loads.get(getState);
	return load && load.loadId === getState().food.loadId ? load : null;
};

/**
 * The current player's budget: when it is not the one the store holds, or the store holds it but
 * has not loaded it (a budget switch clears the store at once), clear the store as needed, restore
 * the browser's copy (FOOD_RESTORED, before any request), then refresh it. When it is loaded or
 * loading, wait for that load, or refresh if the copy is stale. Runs once the player is known and
 * again whenever the budget changes.
 */
export const loadFood = () => (dispatch, getState) => {
	const ids = playerIds(getState());
	if(!ids) {
		return Promise.resolve();
	}
	const holds = () => {
		const food = getState().food;
		return food.userId === ids.userId && food.budgetId === ids.budgetId;
	};
	// Loaded, or loading: nothing to restore.
	const loaded = () => {
		if(!holds()) {
			return null;
		}
		const load = currentLoad(getState);
		if(load) {
			return load.restoring ? load.promise : dispatch(refreshFoodIfStale());
		}
		return null;
	};
	const already = loaded();
	if(already) {
		return already;
	}
	return foodEnvironmentReady().then((env) => {
		const current = playerIds(getState());
		if(!current || current.userId !== ids.userId || current.budgetId !== ids.budgetId) {
			return undefined;
		}
		const settled = loaded();
		if(settled) {
			return settled;
		}
		const { userId, budgetId } = ids;
		if(!holds()) {
			dispatch({ type: FOOD_CLEARED, payload: { userId, budgetId, persistent: env.persisted.persistent } });
		}
		// What the queue knows already, which a cleared store has lost: the indicator counts every budget.
		if(env.queue && typeof env.queue.state === 'function') {
			dispatch(foodSyncState(env.queue.state()));
		}
		const loadId = getState().food.loadId;
		const load = { loadId, promise: null, restoring: true };
		load.promise = openSession(env, userId)
			.then(() => {
				// The session's user's ops wait for nobody but them: a sign-in after a 401 sends them now.
				if(env.queue && env.session) {
					env.queue.resume();
				}
				return env.persisted.removeBelow(FOOD_NAMESPACE, FOOD_SCHEMA);
			})
			.then(() => env.persisted.load(foodKey(userId, budgetId)))
			.catch((error) => {
				console.error(error);
				return null;
			})
			.then((record) => {
				load.restoring = false;
				if(getState().food.loadId !== loadId) {
					return undefined;
				}
				if(record && record.schema === FOOD_SCHEMA && record.confirmed) {
					dispatch({ type: FOOD_RESTORED, payload: { userId, budgetId, record } });
				}
				// Ops and results before the snapshot is asked for, so the snapshot orders after them.
				return dispatch(restoreFoodOps()).then(() => dispatch(refreshFood()));
			});
		loads.set(getState, load);
		return load.promise;
	});
};

/**
 * Fetches the snapshot and replaces what it covers, then fetches whatever months on screen it does
 * not cover, unless `shownRange` is false. Asked for while one is in flight, it runs again once that
 * one is done: never dropped, and never two at once. Asked for while the browser's copy is still
 * being read, it waits for that load, which refreshes once the copy is in. Resolves once the store
 * is current.
 */
export const refreshFood = ({ shownRange = true } = {}) => (dispatch, getState) => {
	if(!isFoodActive(getState)) {
		return Promise.resolve();
	}
	const food = getState().food;
	const load = currentLoad(getState);
	if(load && load.restoring) {
		return load.promise;
	}
	if(food.refreshing) {
		dispatch({ type: FOOD_REFRESH_DIRTY, payload: { budgetId: food.budgetId } });
		return refreshes.get(getState) || Promise.resolve();
	}
	const { budgetId, loadId } = food;
	dispatch({ type: FOOD_SNAPSHOT_BEGIN, payload: { budgetId } });
	const epoch = getState().food.snapshotEpoch;
	const current = () => getState().food.loadId === loadId && getState().food.budgetId === budgetId;
	const request = foodGet('food/snapshot', { budget_id: budgetId })
		.then((snapshot) => {
			if(!current()) {
				return;
			}
			if(!snapshot || Number(snapshot.budget_id) !== budgetId) {
				dispatch({ type: FOOD_SNAPSHOT_FAIL, payload: { budgetId, error: 'The answer was for another budget.' } });
				return;
			}
			dispatch({ type: FOOD_SNAPSHOT_SUCCESS, payload: { budgetId, snapshot, receivedAt: nowSeconds(), epoch } });
		}, (error) => {
			if(current()) {
				console.error(error);
				dispatch({ type: FOOD_SNAPSHOT_FAIL, payload: { budgetId, error: errorMessage(error) } });
			}
		})
		.then(() => {
			if(!current()) {
				return undefined;
			}
			if(getState().food.refreshDirty) {
				return dispatch(refreshFood());
			}
			return shownRange ? dispatch(ensureShownRange()) : undefined;
		});
	refreshes.set(getState, request);
	// Kept until the months on screen have come too, which is after `refreshing` is false again.
	const done = () => {
		if(refreshes.get(getState) === request) {
			refreshes.delete(getState);
		}
	};
	request.then(done, done);
	return request;
};

/** The refresh in flight, or a new one when none is. For a load that only needs the data there. */
export const whenFoodRefreshed = () => (dispatch, getState) => {
	if(isFoodActive(getState) && getState().food.refreshing) {
		return refreshes.get(getState) || Promise.resolve();
	}
	// Still restoring, before its first refresh has started, refreshFood() waits for that load.
	return dispatch(refreshFood());
};

/**
 * refreshFood(options) unless the store was refreshed in the last `maxAge` seconds,
 * STALE_AFTER_SECONDS when not given, or a refresh is in flight, months on screen included. A
 * refresh in flight is waited for rather than asked for again.
 */
export const refreshFoodIfStale = (maxAge = STALE_AFTER_SECONDS, options = {}) => (dispatch, getState) => {
	if(!isFoodActive(getState)) {
		return Promise.resolve();
	}
	const { refreshing, lastSyncedAt } = getState().food;
	const inFlight = refreshes.get(getState);
	if(refreshing || inFlight || (lastSyncedAt && nowSeconds() - lastSyncedAt <= maxAge)) {
		return inFlight || Promise.resolve();
	}
	return dispatch(refreshFood(options));
};

const MONTH = 'YYYY-MM';
const DAY = 'YYYY-MM-DD';

const asDay = (value) => (moment.isMoment(value) ? value.clone() : moment(value, DAY));

/** Whether the store has a current copy of `month` ('YYYY-MM'): inside the window, or a range not stale. */
const monthCovered = (coverage, month) => {
	const first = moment(month, MONTH).startOf('month').format(DAY);
	const last = moment(month, MONTH).endOf('month').format(DAY);
	if(coverage.from && coverage.to && coverage.from <= first && coverage.to >= last) {
		return true;
	}
	return (coverage.ranges || []).some((range) => !range.stale && range.from <= first && range.to >= last);
};

/** Whether a request for `month` ('YYYY-MM') is in flight in this load (the food store's rangesInFlight). */
const monthInFlight = (rangesInFlight, month) => {
	const first = moment(month, MONTH).startOf('month').format(DAY);
	return (rangesInFlight || []).some((range) => range.from <= first && range.to >= first);
};

/**
 * Fetches the whole months from `from` through `to` (moments or 'Y-m-d') that the store has no
 * current copy of: outside the snapshot's window, or kept from before the last snapshot and so stale.
 * A stale month stays on screen while it is fetched again. Waits for the first snapshot, which sets
 * the window.
 *
 * Each request takes an epoch, as a snapshot request does, so a write answered while it is in flight
 * is applied again over the months it brings. A snapshot asked for after it drops it (see the food
 * reducer), and the refresh that snapshot belongs to asks for the months on screen again.
 */
export const ensureFoodRange = (from, to) => (dispatch, getState) => {
	if(!isFoodActive(getState) || !getState().food.coverage) {
		return Promise.resolve();
	}
	const { budgetId, loadId, coverage, rangesInFlight } = getState().food;
	const start = asDay(from).startOf('month');
	const end = asDay(to).startOf('month');
	const months = [];
	for(const month = start.clone(); month.isSameOrBefore(end, 'month'); month.add(1, 'month')) {
		const name = month.format(MONTH);
		if(!monthCovered(coverage, name) && !monthInFlight(rangesInFlight, name)) {
			months.push(name);
		}
	}
	// Runs of consecutive months, at most MAX_RANGE_MONTHS each.
	const runs = [];
	months.forEach((name) => {
		const run = runs[runs.length - 1];
		const follows = run && moment(run[run.length - 1], MONTH).add(1, 'month').format(MONTH) === name;
		if(follows && run.length < MAX_RANGE_MONTHS) {
			run.push(name);
		} else {
			runs.push([name]);
		}
	});
	return Promise.all(runs.map((run) => {
		const rangeFrom = moment(run[0], MONTH).startOf('month').format(DAY);
		const rangeTo = moment(run[run.length - 1], MONTH).endOf('month').format(DAY);
		dispatch({ type: FOOD_RANGE_BEGIN, payload: { budgetId, from: rangeFrom, to: rangeTo } });
		const epoch = getState().food.epoch;
		const current = () => getState().food.loadId === loadId && getState().food.budgetId === budgetId;
		const failed = (error) => {
			if(current()) {
				dispatch({ type: FOOD_RANGE_FAIL, payload: { budgetId, epoch, error } });
			}
		};
		return foodGet('food/range', { budget_id: budgetId, from: rangeFrom, to: rangeTo })
			.then((payload) => {
				if(!payload || Number(payload.budget_id) !== budgetId) {
					failed('The answer was for another budget.');
					return;
				}
				if(current()) {
					dispatch({ type: FOOD_RANGE_LOADED, payload: { budgetId, from: rangeFrom, to: rangeTo, payload, epoch } });
				}
			}, (error) => {
				console.error(error);
				failed(errorMessage(error));
			});
	}));
};

/** ensureFoodRange() for the days the calendar is showing, if it is showing any. */
export const ensureShownRange = () => (dispatch, getState) => {
	const calendar = getState().calendar;
	const start = calendar && calendar.currentStartDate;
	const end = calendar && calendar.currentEndDate;
	if(!moment.isMoment(start) || !moment.isMoment(end)) {
		return Promise.resolve();
	}
	// The calendar's end is the day after the last one it shows.
	return dispatch(ensureFoodRange(start.format(DAY), end.clone().subtract(1, 'day').format(DAY)));
};

/** The bought lists one page of the shopping history shows when not told: Food_Snapshot::DEFAULT_BOUGHT_PAGE. */
export const DEFAULT_BOUGHT_PAGE = 20;

/**
 * A page of the shopping history from food/bought_lists, merged into the store: bought lists older
 * than `before` ('Y-m-d'), or the newest without it; with `query`, only lists with a row whose name
 * contains it; `limit` lists at most, the server's default without one. Resolves with the page, which
 * also says whether there are more (`has_more`) and how many trips and items match in all (`totals`),
 * or null when it failed or is for a budget the store no longer holds.
 */
export const fetchBoughtLists = ({ query = '', before = null, limit = null } = {}) => (dispatch, getState) => {
	if(!isFoodActive(getState)) {
		return Promise.resolve(null);
	}
	const { budgetId, loadId } = getState().food;
	const data = { budget_id: budgetId };
	if(before) {
		data.before = before;
	}
	if(query && query.trim()) {
		data.query = query.trim();
	}
	if(limit) {
		data.limit = limit;
	}
	return Request.apiv1Get('food/bought_lists', data)
		.then((payload) => {
			const food = getState().food;
			if(food.loadId !== loadId || food.budgetId !== budgetId || !payload || Number(payload.budget_id) !== budgetId) {
				return null;
			}
			dispatch({ type: FOOD_BOUGHT_LISTS_LOADED, payload: { budgetId, payload, query: data.query || '' } });
			return payload;
		}, (error) => {
			console.error(error);
			return null;
		});
};

/** The day of the oldest bought list in `entities`, or null. */
const oldestBoughtDay = (entities) => Object.values(entities.groceryLists)
	.map((list) => list.purchased_on)
	.filter(Boolean)
	.sort()[0] || null;

/**
 * The next page of the shopping history, older than the oldest bought list the store holds, or
 * than `before` ('Y-m-d') when given; with `query`, only lists with a row whose name contains it;
 * `limit` lists at most, the server's default without one. Resolves with the page it took, or null.
 */
export const loadOlderBoughtLists = (query = '', before = null, limit = null) => (dispatch, getState) => {
	if(!isFoodActive(getState)) {
		return Promise.resolve(null);
	}
	return dispatch(fetchBoughtLists({ query, before: before || oldestBoughtDay(getState().food.confirmed), limit }));
};

/** Merges an answer's `changes` (the design's shape) for `budgetId`. */
export const foodChangesReceived = (budgetId, changes) => ({
	type: FOOD_CHANGES_RECEIVED,
	payload: { budgetId: Number(budgetId), changes },
});

const withinMs = (promise, ms) => Promise.race([promise, new Promise((resolve) => setTimeout(resolve, ms))]);

/**
 * On logout: empty the store, remove the session marker so no open tab writes another copy, tell
 * the other tabs, clear every store, and delete the database. Never rejects, and gives up after a
 * few seconds, since logging out must not wait on it.
 */
export const forgetFoodData = () => (dispatch) => {
	dispatch({ type: FOOD_CLEARED });
	const forget = foodEnvironmentReady().then((env) => env.persisted.endSession()
		.then(() => {
			env.session = null;
			if(env.queue) {
				env.queue.stop();
			}
			if(env.channel) {
				env.channel.postMessage({ kind: 'logout' });
			}
			return clearDb(env.db);
		})
		.then(() => {
			env.db.close();
			// The stores are already empty, so a delete another connection blocks loses nothing.
			return env.db.persistent ? deleteDb() : undefined;
		}))
		.catch((error) => console.error(error))
		.then(() => {
			setFoodEnvironment(null);
		});
	return withinMs(forget, 3000);
};

/**
 * Refreshes when the browser comes back online, and when the page becomes visible with a copy older
 * than STALE_AFTER_SECONDS; both also read the ops and results the browser holds again. Returns a
 * function that stops watching.
 */
export const watchFood = (dispatch, target = window, doc = document) => {
	const online = () => {
		dispatch(restoreFoodOps());
		dispatch(refreshFood());
	};
	// Results another tab or the service worker stored, for a browser whose tabs cannot tell each other.
	const visible = () => {
		if(doc.visibilityState === 'visible') {
			dispatch(restoreFoodOps());
			dispatch(refreshFoodIfStale());
		}
	};
	target.addEventListener('online', online);
	doc.addEventListener('visibilitychange', visible);
	return () => {
		target.removeEventListener('online', online);
		doc.removeEventListener('visibilitychange', visible);
	};
};

/**
 * Refreshes every FOOD_POLL_SECONDS while the page is visible and the browser online, so a list
 * someone else is ticking off shows their ticks (docs/local-first-food-data.md, step 2f). For the
 * Groceries tab, which starts it while it is the tab shown. Hidden or offline, it stops; becoming
 * visible or coming online starts it again, refreshing at once if the copy is older than a poll.
 *
 * A poll is the snapshot alone: the Groceries tab shows no calendar months, so it does not fetch the
 * ones outside the snapshot that each snapshot marks stale. The calendar asks for those when it is
 * shown again. Never two requests at once: a poll finding a refresh in flight, months on screen
 * included, waits for that one instead, so it neither overlaps it nor queues another after it.
 * Returns a function that stops polling.
 */
export const pollFood = (dispatch, {
	target = window,
	doc = document,
	isOnline = () => typeof navigator === 'undefined' || navigator.onLine !== false,
	every = FOOD_POLL_SECONDS * 1000,
	schedule = (callback, ms) => setTimeout(callback, ms),
	cancel = (timer) => clearTimeout(timer),
} = {}) => {
	let timer = null;
	let stopped = false;
	const snapshotOnly = { shownRange: false };
	const polling = () => !stopped && doc.visibilityState !== 'hidden' && isOnline();
	const stopTimer = () => {
		if(timer !== null) {
			cancel(timer);
			timer = null;
		}
	};
	const tick = () => {
		timer = null;
		if(!polling()) {
			return;
		}
		timer = schedule(tick, every);
		dispatch(refreshFoodIfStale(0, snapshotOnly));
	};
	const start = () => {
		stopTimer();
		if(polling()) {
			timer = schedule(tick, every);
		}
	};
	// Coming back: refresh now if a poll was missed, and poll again from here.
	const resume = () => {
		start();
		if(polling()) {
			dispatch(refreshFoodIfStale(FOOD_POLL_SECONDS, snapshotOnly));
		}
	};
	const visibility = () => (doc.visibilityState === 'hidden' ? stopTimer() : resume());
	target.addEventListener('online', resume);
	target.addEventListener('offline', stopTimer);
	doc.addEventListener('visibilitychange', visibility);
	start();
	return () => {
		stopped = true;
		stopTimer();
		target.removeEventListener('online', resume);
		target.removeEventListener('offline', stopTimer);
		doc.removeEventListener('visibilitychange', visibility);
	};
};

/**
 * Makes a change that must work offline (docs/local-first-food-data.md, phase 3): an op of `type`
 * for the store's budget, shown at once (FOOD_OP_ENQUEUED, applied over `confirmed`) and then
 * written to the browser's queue, which sends it when it can. Resolves with the op as written, or
 * null without a budget.
 */
export const enqueueFoodOp = (type, payload) => (dispatch, getState) => {
	if(!isFoodActive(getState)) {
		return Promise.resolve(null);
	}
	const { userId, budgetId } = getState().food;
	const clientId = uuid();
	const op = createOp({ namespace: FOOD_NAMESPACE, userId, budgetId, type, payload, clientId, now: now() });
	dispatch({ type: FOOD_OP_ENQUEUED, payload: { budgetId, op } });
	return foodEnvironmentReady()
		.then((env) => {
			if(!env.queue) {
				throw new Error('There is no op queue to keep this change in.');
			}
			return env.queue.enqueue(FOOD_NAMESPACE, userId, budgetId, type, payload, { clientId });
		})
		.then((written) => {
			dispatch({ type: FOOD_OP_ENQUEUED, payload: { budgetId, op: written } });
			return written;
		}, (error) => {
			console.error(error);
			return op;
		});
};

/**
 * An op's result, from this tab's sender or another's (the queue's onResult), folded into the store
 * when it is for the budget shown. One for another budget stays in op_results and is folded when
 * that budget is loaded. A replay brings no changes, so the store is refreshed instead.
 */
export const foodOpResult = (record) => (dispatch, getState) => {
	if(!record || record.namespace !== FOOD_NAMESPACE || !isFoodActive(getState)) {
		return;
	}
	const food = getState().food;
	if(Number(record.budget_id) !== food.budgetId || String(record.user_id) !== String(food.userId)) {
		return;
	}
	const result = record.result || {};
	dispatch({ type: result.status === 'rejected' ? FOOD_OP_REJECTED : FOOD_OP_APPLIED, payload: { budgetId: food.budgetId, result: record } });
	if(result.status === 'applied' && result.replayed) {
		dispatch(refreshFood());
	}
};

/** An op another tab made, for the budget shown, so this tab shows it as the other does. */
export const foodOpEnqueuedElsewhere = (op) => (dispatch, getState) => {
	if(!op || op.namespace !== FOOD_NAMESPACE || !isFoodActive(getState) || String(op.user_id) !== String(getState().food.userId)) {
		return;
	}
	dispatch({ type: FOOD_OP_ENQUEUED, payload: { budgetId: Number(op.budget_id), op } });
};

/**
 * The op queue's state for the budget shown, as the store's `sync`; and, for the sync indicator, what
 * it says of every budget: `unsent`, the signed-in user's ops not yet sent in all of them (null until
 * the queue has counted), and `anySending`, whether a request for any of them is in flight.
 */
export const foodSyncState = (queueState) => (dispatch, getState) => {
	if(!isFoodActive(getState) || !queueState) {
		return;
	}
	const budgetId = getState().food.budgetId;
	const groups = queueState.groups || {};
	const group = groups[groupKey(FOOD_NAMESPACE, budgetId)] || {};
	const unsent = queueState.unsent;
	dispatch({
		type: FOOD_SYNC_STATE,
		payload: {
			budgetId,
			sync: {
				sending: !!group.sending,
				lastError: group.lastError || null,
				nextRetryAt: group.nextRetryAt || null,
				paused: queueState.paused || null,
				persistent: queueState.persistent !== false,
				unsent: unsent === null || unsent === undefined ? null : Number(unsent),
				anySending: Object.values(groups).some((each) => !!(each && each.sending)),
			},
		},
	});
};

/**
 * The ops and results the browser holds for the budget shown, read together: results folded first
 * (each once), then the ops still unsent as `pending`. On load, before the snapshot is asked for, and
 * when the page is shown again, which is how a tab without BroadcastChannel hears of another's. An op
 * shown as pending here that is now neither held nor answered was sent by another tab whose result
 * is gone already, so only a refresh can show it. A refusal the user has not been told of is told.
 */
export const restoreFoodOps = () => (dispatch, getState) => {
	if(!isFoodActive(getState)) {
		return Promise.resolve();
	}
	const { budgetId, loadId } = getState().food;
	return foodEnvironmentReady()
		.then((env) => (env.queue && env.session ? env.queue.load(FOOD_NAMESPACE, budgetId) : null))
		.then((held) => {
			if(!held || getState().food.loadId !== loadId) {
				return undefined;
			}
			const before = getState().food.pending;
			dispatch({ type: FOOD_RESULTS_FOLDED, payload: { budgetId, results: held.results } });
			dispatch({ type: FOOD_OPS_RESTORED, payload: { budgetId, ops: held.ops } });
			// Refusals no tab here was sent: by the service worker, or by a tab since closed.
			dispatch(tellFoodRejections(held.results));
			const after = getState().food;
			const vanished = before.some((op) => op.seq !== null && op.seq !== undefined && !after.folded[op.client_id] &&
				!after.pending.some((each) => each.client_id === op.client_id));
			return vanished ? dispatch(refreshFood()) : undefined;
		})
		.catch((error) => console.error(error));
};

/**
 * Deletes the op results folded into the store before the snapshot of `snapshotEpoch` was applied,
 * once that snapshot is in the browser's copy: the copy holds what they changed from then on. A
 * rejected result stays until it is dismissed. persistFood() calls it after each save, with the
 * `loadId` the saved copy was taken from: epochs start again with each load, so after a budget
 * switch meanwhile the saved epoch says nothing of the results folded now, and nothing is deleted.
 */
export const pruneFoldedResults = (snapshotEpoch, loadId) => (dispatch, getState) => {
	if(!isFoodActive(getState) || getState().food.loadId !== loadId) {
		return Promise.resolve();
	}
	const { budgetId, folded } = getState().food;
	const clientIds = Object.keys(folded).filter((id) => folded[id].status === 'applied' && folded[id].epoch < snapshotEpoch);
	if(!clientIds.length) {
		return Promise.resolve();
	}
	return foodEnvironmentReady()
		.then((env) => (env.queue ? env.queue.removeResults(clientIds) : undefined))
		.then(() => {
			dispatch({ type: FOOD_RESULTS_PRUNED, payload: { budgetId, clientIds } });
		})
		.catch((error) => console.error(error));
};

/** Rejections told in this tab, by client_id, while their `told` mark is being written. */
const toldHere = new Set();

/**
 * Tells the user, once, of the changes the server refused among `records` (op results): those for
 * the budget shown that nobody has told them of yet. The tab whose sender got the answer tells at
 * once; one sent by the service worker, or by a tab since closed, is told on the next load or return
 * to the page. Each is then marked `told` in op_results, so no reload or other tab tells it again.
 * It stays in `food.rejected` until dismissed.
 */
export const tellFoodRejections = (records) => (dispatch, getState) => {
	if(!isFoodActive(getState)) {
		return Promise.resolve();
	}
	const food = getState().food;
	const untold = (records || []).filter((record) => record && record.namespace === FOOD_NAMESPACE &&
		record.result && record.result.status === 'rejected' && !record.told && !toldHere.has(record.client_id) &&
		Number(record.budget_id) === food.budgetId && String(record.user_id) === String(food.userId));
	if(!untold.length) {
		return Promise.resolve();
	}
	const clientIds = untold.map((record) => record.client_id);
	clientIds.forEach((id) => toldHere.add(id));
	try {
		queueHooks.onRejected(untold, food.entities);
	} catch(error) {
		console.error(error);
	}
	return foodEnvironmentReady()
		.then((env) => (env.queue ? env.queue.markResults(clientIds, { told: true }) : undefined))
		.catch((error) => console.error(error));
};

/** Forgets rejected changes the user has seen, here and in the browser's results. */
export const dismissRejectedFoodOps = (clientIds) => (dispatch, getState) => {
	if(!isFoodActive(getState)) {
		return Promise.resolve();
	}
	dispatch({ type: FOOD_REJECTED_DISMISSED, payload: { budgetId: getState().food.budgetId, clientIds } });
	return foodEnvironmentReady()
		.then((env) => (env.queue ? env.queue.removeResults(clientIds) : undefined))
		.catch((error) => console.error(error));
};

/** How many of the signed-in user's changes have not been sent, in every budget. */
export const unsentFoodOps = () => (dispatch, getState) => {
	const ids = playerIds(getState());
	return foodEnvironmentReady()
		.then((env) => (env.queue ? env.queue.count(ids ? ids.userId : undefined) : 0))
		.catch((error) => {
			console.error(error);
			return 0;
		});
};

/**
 * Before logging out: whether to go ahead. Asks, through `confirm`, when changes have not been sent,
 * since logging out deletes them. Resolves true when there are none.
 */
export const confirmLosingUnsentFoodOps = (confirm = (message) => window.confirm(message)) => (dispatch) => dispatch(unsentFoodOps())
	.then((count) => count === 0 || confirm(`${count} change${count === 1 ? ' has' : 's have'} not been sent and will be lost. Log out anyway?`));

/**
 * The online-only barrier for the budget shown: resolves once its queued ops are all sent, at once
 * when there are none, and rejects with "Needs a connection" while they cannot be sent.
 */
export const foodOpsDrained = () => (dispatch, getState) => {
	if(!isFoodActive(getState)) {
		return Promise.resolve();
	}
	const budgetId = getState().food.budgetId;
	return foodEnvironmentReady().then((env) => (env.queue ? env.queue.drained(FOOD_NAMESPACE, budgetId) : undefined));
};
